import { useState, type ReactNode } from "react";

import {
    CollapseExpandButton,
    CollapseTransition,
    InfoTooltip,
    PendoId,
    PendoProps,
    Section,
} from "@impero-com/ui";

import * as theme from "./ControlHealthWidget.css";

interface ControlHealthWidgetProps extends PendoProps {
    collapsePendoId?: PendoId;
    title: string;
    /** Explains the widget from a question mark beside the title. */
    info?: string;
    /** Controls shown beside the title. */
    actions?: ReactNode;
    /** The widget's anchor figure, shown between the title and the body. */
    headline?: ReactNode;
    /** Gives the widget a chevron that folds the body away. */
    collapsible?: boolean;
    /** The card style, so each widget can size itself. */
    className: string;
    children: ReactNode;
}

/** The card every Control Health dashboard widget is drawn on. */
export function ControlHealthWidget({
    title,
    info,
    actions,
    headline,
    collapsible = false,
    className,
    children,
    "data-pendo": dataPendo,
    collapsePendoId,
}: ControlHealthWidgetProps) {
    const [collapsed, setCollapsed] = useState(false);

    return (
        <Section className={className} data-pendo={dataPendo}>
            <div className={theme.top}>
                <div className={theme.header}>
                    <div className={theme.titleRow}>
                        <h2 className={theme.title}>{title}</h2>
                        {info !== undefined && (
                            <InfoTooltip size="small" text={info} />
                        )}
                    </div>
                    {(actions || collapsible) && (
                        <div className={theme.controls}>
                            {actions}
                            {collapsible && (
                                <CollapseExpandButton
                                    data-pendo={collapsePendoId}
                                    collapsed={collapsed}
                                    onClick={() => setCollapsed(!collapsed)}
                                />
                            )}
                        </div>
                    )}
                </div>
                {headline}
            </div>
            {collapsible ? (
                <CollapseTransition collapsed={collapsed}>
                    {children}
                </CollapseTransition>
            ) : (
                children
            )}
        </Section>
    );
}
